import { BannerInterface } from '@/api/banner';
import { defineStore } from 'pinia';
import { type Ref, computed, ref } from 'vue';

type BannerType = 'panel' | 'login_register';

const BANNER_HIDE_PREFIX = 'next_show_time_banner_';

export const useBannersStore = defineStore('banners', () => {
  const panelBanner: Ref<BannerInterface | undefined> = ref();
  const authBanner: Ref<BannerInterface | undefined> = ref();
  const panelBannerVisible = ref(true);
  const authBannerVisible = ref(true);
  const activeBannerType = ref<BannerType | undefined>(undefined);
  let panelBannerTimer: number | undefined;
  let authBannerTimer: number | undefined;

  const getLocalStorageKey = (type: BannerType) => `${BANNER_HIDE_PREFIX}${type}`;

  const getHideRemainingMs = (type: BannerType, banner: BannerInterface): number | undefined => {
    const key = getLocalStorageKey(type);
    const hiddenData = localStorage.getItem(key);

    if (!hiddenData) {
      return undefined;
    }

    try {
      const { timestamp } = JSON.parse(hiddenData);
      const elapsedTime = Date.now() - timestamp;
      const reShowTimeMs = banner.reshow_time * 60 * 1000;

      if (elapsedTime >= reShowTimeMs) {
        localStorage.removeItem(key);
        return 0;
      }

      return reShowTimeMs - elapsedTime;
    } catch {
      localStorage.removeItem(key);
      return undefined;
    }
  };

  const canShowBanner = (type: BannerType, banner: BannerInterface): boolean => {
    const remainingMs = getHideRemainingMs(type, banner);
    return remainingMs === undefined || remainingMs <= 0;
  };

  const activeBanner = computed<BannerInterface | undefined>(() => {
    if (activeBannerType.value === 'login_register' && authBanner.value && authBannerVisible.value) {
      return authBanner.value;
    }

    if (activeBannerType.value === 'panel' && panelBanner.value && panelBannerVisible.value) {
      return panelBanner.value;
    }

    if (authBanner.value && authBannerVisible.value) {
      return authBanner.value;
    }

    if (panelBanner.value && panelBannerVisible.value) {
      return panelBanner.value;
    }

    return undefined;
  });

  const clearBannerTimer = (type: BannerType) => {
    if (type === 'panel' && panelBannerTimer !== undefined) {
      clearTimeout(panelBannerTimer);
      panelBannerTimer = undefined;
    }

    if (type === 'login_register' && authBannerTimer !== undefined) {
      clearTimeout(authBannerTimer);
      authBannerTimer = undefined;
    }
  };

  const scheduleBannerReshow = (type: BannerType, banner: BannerInterface) => {
    clearBannerTimer(type);

    const remainingMs = getHideRemainingMs(type, banner);
    const delay = remainingMs !== undefined ? remainingMs : banner.reshow_time * 60 * 1000;
    if (delay <= 0) {
      return;
    }

    const showFn = () => {
      if (type === 'panel') {
        showPanelBanner(banner);
      } else {
        showAuthBanner(banner);
      }
    };

    if (type === 'panel') {
      panelBannerTimer = window.setTimeout(showFn, delay);
    } else {
      authBannerTimer = window.setTimeout(showFn, delay);
    }
  };

  const showPanelBanner = (banner: BannerInterface) => {
    clearBannerTimer('panel');
    if (!canShowBanner('panel', banner)) {
      const remainingMs = getHideRemainingMs('panel', banner);
      if (remainingMs !== undefined && remainingMs > 0) {
        scheduleBannerReshow('panel', banner);
      }
      return; // Banner is in hide period, don't show
    }
    panelBanner.value = banner;
    panelBannerVisible.value = true;
    activeBannerType.value = 'panel';
  };

  const showAuthBanner = (banner: BannerInterface) => {
    clearBannerTimer('login_register');
    if (!canShowBanner('login_register', banner)) {
      const remainingMs = getHideRemainingMs('login_register', banner);
      if (remainingMs !== undefined && remainingMs > 0) {
        scheduleBannerReshow('login_register', banner);
      }
      return; // Banner is in hide period, don't show
    }
    authBanner.value = banner;
    authBannerVisible.value = true;
    activeBannerType.value = 'login_register';
  };

  const setActiveBanner = (type: BannerType) => {
    activeBannerType.value = type;
  };

  const hide = () => {
    if (activeBannerType.value === 'login_register') {
      authBannerVisible.value = false;
      if (authBanner.value) {
        const key = getLocalStorageKey('login_register');
        localStorage.setItem(key, JSON.stringify({ timestamp: Date.now() }));
        scheduleBannerReshow('login_register', authBanner.value);
      }
    }

    if (activeBannerType.value === 'panel') {
      panelBannerVisible.value = false;
      if (panelBanner.value) {
        const key = getLocalStorageKey('panel');
        localStorage.setItem(key, JSON.stringify({ timestamp: Date.now() }));
        scheduleBannerReshow('panel', panelBanner.value);
      }
    }

    activeBannerType.value = undefined;
  };

  const reset = () => {
    clearBannerTimer('panel');
    clearBannerTimer('login_register');
    panelBannerVisible.value = true;
    authBannerVisible.value = true;
    activeBannerType.value = undefined;
  };

  return {
    panelBanner,
    authBanner,
    activeBanner,
    showPanelBanner,
    showAuthBanner,
    setActiveBanner,
    hide,
    reset,
  };
});
